import {
  InfiniteData,
  QueryKey,
  useInfiniteQuery,
  useMutation,
  useQuery,
} from '@tanstack/react-query';
import { useApiClient } from '../contexts/ApiContext';
import { ApiTransactionListRequestQuery, ApiTransactionListResponse } from '../openapi';
import { removeEmptyFromObject } from '../utils/removeEmptyFromObject';
import { downloadUrl } from '@razz-team/utils';
import { mapResponse } from './helpers';

export function useTransactionsInfinityQuery(
  query: Omit<ApiTransactionListRequestQuery, 'page'> = {},
) {
  const client = useApiClient();
  return useInfiniteQuery<
    ApiTransactionListResponse,
    Error,
    InfiniteData<ApiTransactionListResponse, string | undefined>,
    QueryKey,
    string | undefined
  >({
    queryKey: ['transactions', query],
    queryFn: ({ pageParam, signal }) =>
      client
        .GET('/transactions', {
          signal,
          params: {
            query: {
              ...removeEmptyFromObject(query),
              page: pageParam,
            },
          },
        })
        .then(mapResponse),
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => lastPage?.nextPage,
  });
}

export function useDownloadTransactionStatementMutation() {
  const client = useApiClient();

  return useMutation({
    mutationKey: ['transactions', 'statement'],
    mutationFn: ({ createdAt, transactionId }: { createdAt: string; transactionId: string }) =>
      client
        .GET('/transactions/{id}/statement', {
          params: {
            path: {
              id: transactionId,
            },
            query: {
              createdAt,
            },
          },
        })
        .then(({ data }) => {
          if (data?.link) {
            downloadUrl(data.link);
          }
        }),
  });
}

export function useTransactionQuery({
  transactionId,
  createdAt,
}: {
  transactionId: string | undefined;
  createdAt: string | undefined;
}) {
  const client = useApiClient();
  return useQuery({
    queryKey: ['transactions', transactionId, createdAt],
    queryFn: () =>
      client
        .GET('/transactions/{id}', {
          params: { path: { id: transactionId! }, query: { createdAt: createdAt! } },
        })
        .then(mapResponse),
    enabled: Boolean(transactionId && createdAt),
  });
}
